'use client';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SearchInput from '@dr-pam/common-components/Components/Form/SearchInput';
import GoogleMap, { GoogleMapMarker } from './GoogleMap';
import Button from '@dr-pam/common-components/Components/Form/Button';
import {
	OnPlaceChangedHandler,
	useGoogleMapsAutocomplete,
	useGoogleMapsService,
} from '@dr-pam/common-components/Services/GoogleMapsService';
import { useFindPractitionerService } from '../../services/FindPractitionerService';
import Loading from '@dr-pam/common-components/Components/Misc/Loading';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import { SearchRequest } from '../../app/find-an-ndc-practitioner/search/route';
import Practitioner, { PublicProfile } from './Practitioner';
import ReactDOMServer from 'react-dom/server';
import TextInput from '@dr-pam/common-components/Components/Form/TextInput';
import { useDebouncedState } from '@mantine/hooks';
import hash from 'object-hash';

export type FindPractitionerProps = {
	className?: string;
};

export default function FindPractitioner(props: FindPractitionerProps) {
	const { className } = props;

	const radiusInputEl = useRef<HTMLInputElement | null>(null);
	const [searchEl, setSearchEl] = useState<HTMLInputElement | null>(null);
	const [searchValue, setSearchValue] = useState<string>('');
	const [searchLocation, setSearchLocation] = useState<google.maps.LatLngLiteral | undefined>(undefined);
	const [isGettingLocation, setIsGettingLocation] = useState(false);
	const [searchResults, setSearchResults] = useState<PublicProfile[] | undefined>(undefined);
	const [selectedResult, setSelectedResult] = useState<PublicProfile | undefined>(undefined);
	const [approxLocation, setApproxLocation] = useState<google.maps.LatLngLiteral | undefined>(undefined);
	const [maxDistanceKm, setMaxDistanceKm] = useDebouncedState('25', 500);

	const { isLoading, addLoader, removeLoader } = useLoadTracker();
	const googleMapsService = useGoogleMapsService();
	const findPractitionerService = useFindPractitionerService();

	// Gets the approx location of the user
	useEffect(() => {
		const request = googleMapsService.getApproximateLocation();
		request.response
			.then((location) =>
				setApproxLocation({
					lat: location.latitude,
					lng: location.longitude,
				}),
			)
			.catch(() => {
				/*ignore*/
			});
	}, [googleMapsService]);

	const executeSearch = useCallback(
		async (search: SearchRequest) => {
			const loader = addLoader();
			try {
				const request = findPractitionerService.search(search);
				const results = await request.response;

				// sort search results by distance
				const sortedResults = await googleMapsService.sortByDistanceFromPoint(
					{ lat: search.lat, lng: search.long },
					results,
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					(x) => x.publicAddress.location!,
				);
				setSearchResults(sortedResults);
			} catch (err) {
				NotificationUtils.showError(err as Error, 'Error');
			} finally {
				removeLoader(loader);
			}
		},
		[addLoader, findPractitionerService, googleMapsService, removeLoader],
	);

	const handleUseLocationClicked = async () => {
		try {
			setIsGettingLocation(true);
			const currentPosition = await googleMapsService.getCurrentPosition();
			if (currentPosition) {
				setSearchLocation({
					lat: currentPosition.coords.latitude,
					lng: currentPosition.coords.longitude,
				});
			}
		} catch {
			NotificationUtils.showError(
				new Error('Please enable the Location permission.'),
				'Unable to get current location',
			);
		} finally {
			setIsGettingLocation(false);
		}
	};

	const onPlaceChanged: OnPlaceChangedHandler = useCallback((place) => {
		const location = place.geometry?.location;
		if (location) {
			setSearchLocation({
				lat: place.geometry?.location?.lat() ?? 0,
				lng: place.geometry?.location?.lng() ?? 0,
			});
		} else {
			NotificationUtils.showError(new Error('Unable to find location'), 'Error');
		}
		setSearchValue(place.formatted_address ?? '');
	}, []);

	const handleMarkerChanged = useCallback(
		(marker: GoogleMapMarker | null) => {
			if (marker) {
				const result = searchResults?.find((x) => hash(x) === marker.id);
				if (result) {
					setSelectedResult(result);
				}
			}
		},
		[searchResults],
	);

	const { isLoading: isGoogleLoading } = useGoogleMapsAutocomplete(searchEl, onPlaceChanged);

	const mapMarkers = useMemo(() => {
		if (!searchResults) {
			return [];
		}
		return searchResults.map<GoogleMapMarker>((result) => {
			return {
				id: hash(result),
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				location: result.publicAddress.location!,
				title: `${result.publicName} (${result.publicDiscipline})`,
				infoWindow: ReactDOMServer.renderToString(<Practitioner profile={result} />),
			};
		});
	}, [searchResults]);

	const selectedMapMarker = useMemo(() => {
		if (selectedResult) {
			return mapMarkers.find((x) => x.id === hash(selectedResult));
		}
		return undefined;
	}, [mapMarkers, selectedResult]);

	const currentMaxDistanceKm = useMemo(() => {
		let maxDistance = parseInt(maxDistanceKm, 10);
		if (isNaN(maxDistance) || maxDistance <= 0) {
			maxDistance = 25;
		}
		return maxDistance;
	}, [maxDistanceKm]);

	useEffect(() => {
		if (searchLocation) {
			executeSearch({
				lat: searchLocation.lat,
				long: searchLocation.lng,
				maxDistanceKm: currentMaxDistanceKm,
			});
		}
	}, [currentMaxDistanceKm, executeSearch, searchLocation]);

	const handlePractitionerClicked = (profile: PublicProfile) => {
		setSelectedResult(profile);
	};

	return (
		<div className={`FindPractitioner ${className ?? ''}`}>
			<p>
				Are you looking for a registered health professional who is trained to deliver NDC or the Possums
				programs? Are you needing help with breastfeeding, infant feeding, sleep, unsettled infant behaviour or
				perinatal mental health challenges but want to be sure you see someone who holds active NDC
				qualifications and is a part of the NDC or Possums professional networks?
			</p>
			<p>
				Let&apos;s find an NDC accredited health professional near you. Start by using your current location, or
				searching for an address.
			</p>

			{isGoogleLoading && <Loading />}
			<div className="function-container" style={{ visibility: isGoogleLoading ? 'hidden' : 'visible' }}>
				<div className="search-wrapper">
					<Button
						onClick={handleUseLocationClicked}
						disabled={isGettingLocation}
						isLoading={isGettingLocation}
					>
						Use my current location
					</Button>
					<div className="tac">or</div>
					<SearchInput
						className="fg1"
						onChange={setSearchValue}
						value={searchValue}
						ref={setSearchEl}
						disabled={isGettingLocation}
						placeholder="Search for address or city"
					/>
					<div className="divider"></div>
					<div className="filter-wrapper">
						<div className="search-radius-wrapper">
							<span>Search radius:</span>
							<TextInput
								ref={radiusInputEl}
								className="search-radius"
								type="number"
								value={maxDistanceKm}
								onChange={setMaxDistanceKm}
							/>
						</div>
					</div>
				</div>
				<div
					className={`results-list ${
						searchResults ? (searchResults.length === 0 ? 'no-results' : 'has-results') : ''
					}`}
				>
					{isLoading ? (
						<Loading>Searching for practitioners...</Loading>
					) : (
						searchResults &&
						(searchResults.length ? (
							<>
								<p>Here are some NDC practitioners near you:</p>
								{searchResults.map((result) => {
									const resultHash = hash(result);
									return (
										<Practitioner
											key={resultHash}
											className={
												selectedResult && hash(selectedResult) === resultHash ? 'selected' : ''
											}
											profile={result}
											onClick={() => handlePractitionerClicked(result)}
										/>
									);
								})}
							</>
						) : (
							<>
								<p>
									Sorry, we couldn&apos;t locate any NDC practitioners within {currentMaxDistanceKm}
									km of you. Try expanding the search radius.
								</p>
							</>
						))
					)}
				</div>
				<GoogleMap
					centerOn={searchLocation ?? approxLocation}
					initialPositionMarker={searchLocation}
					initialPositionRadiusKm={currentMaxDistanceKm}
					markers={mapMarkers}
					selectedMarker={selectedMapMarker}
					onMarkerChanged={handleMarkerChanged}
					className="map-container"
				/>
				<div className="fineprint">
					{' '}
					<p>
						Please note: If a health professional cannot be found here, it is most likely they are not
						qualified to identify as NDC or Possums practitioners. The Possums (or NDC) programs are
						constantly updating, jumping the gap between evidence and practice. The only professionals who
						are in constant and updated contact with the evolving evidence-base, translated into programs
						through the Possums frameworks, are those who are members of The NDC Institute.
					</p>
				</div>
			</div>
		</div>
	);
}
